import { Outlet, NavLink, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore, useDashboardStore } from '../../lib/store';
import {
    LayoutDashboard,
    Map,
    BarChart3,
    LineChart,
    FileText,
    LogOut,
    Menu,
    X,
    Globe,
    ChevronDown,
    PanelLeftClose,
    PanelLeftOpen,
} from 'lucide-react';
import { useState } from 'react';
import logoApp from '../../assets/logo-app.svg';

export default function DashboardLayout() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { user, logout } = useAuthStore();
    const { sidebarOpen, toggleSidebar, sidebarCollapsed, toggleSidebarCollapsed } = useDashboardStore();
    const [langDropdownOpen, setLangDropdownOpen] = useState(false);
    const [userDropdownOpen, setUserDropdownOpen] = useState(false);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const changeLanguage = (lang: string) => {
        i18n.changeLanguage(lang);
        setLangDropdownOpen(false);
    };

    const navItems = [
        { path: '/', icon: LayoutDashboard, label: t('nav.dashboard') },
        { path: '/polygons', icon: Map, label: t('nav.polygons') },
        { path: '/analysis', icon: BarChart3, label: t('nav.analysis') },
        { path: '/analytics', icon: LineChart, label: t('nav.analytics') },
        { path: '/reports', icon: FileText, label: t('nav.reports') },
    ];

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            {/* Sidebar */}
            <aside
                className={`fixed top-0 left-0 z-40 h-screen transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
                    } md:translate-x-0`}
            >
                <div className={`h-full ${sidebarCollapsed ? 'w-64 md:w-16' : 'w-64'} bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col transition-[width] duration-300`}>
                    {/* Logo */}
                    <div className={`border-b border-gray-200 dark:border-gray-700 ${sidebarCollapsed ? 'p-6 md:p-3' : 'p-6'}`}>
                        <div className={`flex items-center gap-3 ${sidebarCollapsed ? 'md:justify-center md:gap-0' : ''}`}>
                            <img src={logoApp} alt="Hit the Land" className="w-10 h-10 rounded-xl flex-shrink-0" />
                            <div className={sidebarCollapsed ? 'md:hidden' : ''}>
                                <h1 className="font-display font-bold text-lg text-gray-900 dark:text-white">
                                    Hit the Land
                                </h1>
                                <p className="text-xs text-gray-500 dark:text-gray-400">EUDR Platform</p>
                            </div>
                        </div>
                    </div>

                    {/* Navigation */}
                    <nav className="flex-1 p-4 space-y-1">
                        {navItems.map((item) => (
                            <NavLink
                                key={item.path}
                                to={item.path}
                                end={item.path === '/'}
                                title={item.label}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${sidebarCollapsed ? 'md:justify-center md:px-2' : ''} ${isActive
                                        ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 font-medium'
                                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                    }`
                                }
                            >
                                <item.icon className="w-5 h-5 flex-shrink-0" />
                                <span className={sidebarCollapsed ? 'md:hidden' : ''}>{item.label}</span>
                            </NavLink>
                        ))}
                    </nav>

                    {/* Footer */}
                    <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                        <button
                            onClick={handleLogout}
                            title={t('nav.logout')}
                            className={`flex items-center gap-3 w-full px-4 py-3 rounded-xl text-gray-600 dark:text-gray-300 hover:bg-red-50 dark:hover:bg-red-900/30 hover:text-red-600 dark:hover:text-red-400 transition-all duration-200 ${sidebarCollapsed ? 'md:justify-center md:px-2' : ''}`}
                        >
                            <LogOut className="w-5 h-5 flex-shrink-0" />
                            <span className={sidebarCollapsed ? 'md:hidden' : ''}>{t('nav.logout')}</span>
                        </button>
                        <p className={`mt-3 px-4 text-center text-xs text-gray-400 dark:text-gray-500 ${sidebarCollapsed ? 'md:hidden' : ''}`}>
                            <Link to="/terms" className="hover:text-gray-600 dark:hover:text-gray-300">
                                {t('legal.terms')}
                            </Link>
                            <span className="mx-1.5">·</span>
                            <Link to="/privacy" className="hover:text-gray-600 dark:hover:text-gray-300">
                                {t('legal.privacy')}
                            </Link>
                        </p>
                    </div>
                </div>
            </aside>

            {/* Main content — desktop always leaves room for the fixed sidebar
                (full or collapsed rail); on mobile the sidebar is an overlay. */}
            <div className={`${sidebarCollapsed ? 'md:ml-16' : 'md:ml-64'} transition-all duration-300`}>
                {/* Top bar */}
                <header className="sticky top-0 z-30 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between px-4 sm:px-6 py-4">
                        {/* Mobile menu button */}
                        <button
                            onClick={toggleSidebar}
                            className="md:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                        </button>
                        {/* Desktop collapse toggle */}
                        <button
                            onClick={toggleSidebarCollapsed}
                            className="hidden md:inline-flex p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            title={sidebarCollapsed ? 'Expandir menú' : 'Colapsar menú'}
                            aria-label="Toggle sidebar"
                        >
                            {sidebarCollapsed ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
                        </button>

                        {/* Right side */}
                        <div className="flex items-center gap-4">
                            {/* Language switcher */}
                            <div className="relative">
                                <button
                                    onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                                    className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                >
                                    <Globe className="w-5 h-5 text-gray-500" />
                                    <span className="text-sm font-medium uppercase">{i18n.language}</span>
                                    <ChevronDown className="w-4 h-4" />
                                </button>
                                {langDropdownOpen && (
                                    <div className="absolute right-0 mt-2 w-32 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 py-2">
                                        <button
                                            onClick={() => changeLanguage('es')}
                                            className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700"
                                        >
                                            Español
                                        </button>
                                        <button
                                            onClick={() => changeLanguage('en')}
                                            className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700"
                                        >
                                            English
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* User menu */}
                            <div className="relative">
                                <button
                                    onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                                    className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                >
                                    <div className="w-8 h-8 rounded-full bg-primary-700 flex items-center justify-center text-white font-medium">
                                        {user?.full_name?.charAt(0) || 'U'}
                                    </div>
                                    <div className="hidden md:block text-left">
                                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                                            {user?.full_name}
                                        </p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            {user?.tenant_name}
                                        </p>
                                    </div>
                                    <ChevronDown className="w-4 h-4 hidden md:block" />
                                </button>
                                {userDropdownOpen && (
                                    <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 py-2">
                                        <button
                                            onClick={handleLogout}
                                            className="flex items-center gap-2 w-full text-left px-4 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
                                        >
                                            <LogOut className="w-4 h-4" />
                                            {t('nav.logout')}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </header>

                {/* Page content */}
                <main className="p-4 sm:p-6">
                    <Outlet />
                </main>
            </div>

            {/* Overlay for mobile sidebar */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 z-30 bg-black/50 md:hidden"
                    onClick={toggleSidebar}
                />
            )}
        </div>
    );
}
